import { useCallback, useEffect, useMemo, useState } from "react";

const constrainKeys = (keys: Iterable<string>, validKeys: Set<string>): Set<string> =>
  new Set([...keys].filter(key => validKeys.has(key)));

const sameSet = (a: Set<string>, b: Set<string>): boolean =>
  a.size === b.size && [...a].every(key => b.has(key));

export const useConstrainedExpandedKeys = (
  validKeys: string[],
  initialExpandedKeys: Iterable<string> = [],
) => {
  const validKeySet = useMemo(() => new Set(validKeys), [validKeys]);
  const [expandedKeys, setExpandedKeysState] = useState<Set<string>>(() =>
    constrainKeys(initialExpandedKeys, validKeySet),
  );

  useEffect(() => {
    setExpandedKeysState(current => {
      const next = constrainKeys(current, validKeySet);
      return sameSet(current, next) ? current : next;
    });
  }, [validKeySet]);

  const setExpandedKeys = useCallback(
    (keys: Iterable<string>) => {
      setExpandedKeysState(constrainKeys(keys, validKeySet));
    },
    [validKeySet],
  );

  const toggleKey = useCallback(
    (key: string) => {
      setExpandedKeysState(current => {
        if (!validKeySet.has(key)) return current;
        const next = new Set(current);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [validKeySet],
  );

  return { expandedKeys, setExpandedKeys, toggleKey };
};
